import productoService from '../../../services/productoService';
import type { ProductoInfo360Maestro } from './components/ArticuloInfo360Header';
import type { ArticuloInfo360Indicadores } from './types';

const TTL_MS = 90_000;

export interface Info360ProductoCacheEntry {
  maestro: ProductoInfo360Maestro | null;
  indicadores: ArticuloInfo360Indicadores | null;
  maestroFetchedAt?: number;
  indicadoresFetchedAt?: number;
}

const cache = new Map<string, Info360ProductoCacheEntry>();
const prefetchTimers = new Map<string, ReturnType<typeof setTimeout>>();

function cacheKey(codigo: string): string {
  return codigo.trim().toUpperCase();
}

export function getCachedInfo360(codigo: string): Info360ProductoCacheEntry | null {
  return cache.get(cacheKey(codigo)) ?? null;
}

export function isCacheFresh(
  entry: Info360ProductoCacheEntry,
  field: 'maestro' | 'indicadores',
): boolean {
  const ts = field === 'maestro' ? entry.maestroFetchedAt : entry.indicadoresFetchedAt;
  if (!ts) return false;
  return Date.now() - ts < TTL_MS;
}

export function setCachedMaestro(codigo: string, maestro: ProductoInfo360Maestro): void {
  const k = cacheKey(codigo);
  const prev = cache.get(k) ?? { maestro: null, indicadores: null };
  cache.set(k, { ...prev, maestro, maestroFetchedAt: Date.now() });
}

export function setCachedIndicadores(codigo: string, indicadores: ArticuloInfo360Indicadores): void {
  const k = cacheKey(codigo);
  const prev = cache.get(k) ?? { maestro: null, indicadores: null };
  cache.set(k, { ...prev, indicadores, indicadoresFetchedAt: Date.now() });
}

export function invalidateInfo360Cache(codigo?: string): void {
  if (codigo) {
    cache.delete(cacheKey(codigo));
    return;
  }
  cache.clear();
}

async function prefetchMaestro(codigo: string): Promise<void> {
  const k = cacheKey(codigo);
  const entry = cache.get(k);
  if (entry?.maestro && isCacheFresh(entry, 'maestro')) return;
  try {
    const data = await productoService.getProducto(codigo, { soloOperativo: false });
    setCachedMaestro(codigo, data as ProductoInfo360Maestro);
  } catch {
    /* prefetch silencioso */
  }
}

async function prefetchIndicadores(codigo: string): Promise<void> {
  const k = cacheKey(codigo);
  const entry = cache.get(k);
  if (entry?.indicadores && isCacheFresh(entry, 'indicadores')) return;
  try {
    const data = await productoService.getProductoInfo360(codigo);
    setCachedIndicadores(codigo, data);
  } catch {
    /* prefetch silencioso */
  }
}

/** Prefetch al hover/focus del botón Info 360 (debounce 150 ms). */
export function prefetchInfo360(codigo: string, debounceMs = 150): void {
  const trimmed = codigo.trim();
  if (!trimmed) return;

  const k = cacheKey(trimmed);
  const existing = prefetchTimers.get(k);
  if (existing) clearTimeout(existing);

  prefetchTimers.set(k, setTimeout(() => {
    prefetchTimers.delete(k);
    void prefetchMaestro(trimmed);
    void prefetchIndicadores(trimmed);
  }, debounceMs));
}
